/** Small, dependency-free Markdown renderer shared by simulation reports and Agent chat. */
export function renderMarkdown(markdown: string): string {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const html: string[] = [];
  const paragraph: string[] = [];
  let listTag: 'ul' | 'ol' | null = null;
  let inCodeBlock = false;
  let codeLines: string[] = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    html.push(`<p>${renderInline(paragraph.join(' '))}</p>`);
    paragraph.length = 0;
  };

  const closeList = () => {
    if (!listTag) return;
    html.push(`</${listTag}>`);
    listTag = null;
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      flushParagraph();
      closeList();
      if (inCodeBlock) {
        html.push(`<pre><code>${escapeHtml(codeLines.join('\n'))}</code></pre>`);
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    if (!trimmed) {
      flushParagraph();
      closeList();
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      closeList();
      const level = heading[1].length;
      html.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      continue;
    }

    const quote = trimmed.match(/^>\s?(.*)$/);
    if (quote) {
      flushParagraph();
      closeList();
      html.push(`<blockquote>${renderInline(quote[1])}</blockquote>`);
      continue;
    }

    const tableHeader = parseTableRow(trimmed);
    const tableSeparator = parseTableRow(lines[lineIndex + 1]?.trim() || '');
    if (tableHeader.length && isTableSeparator(tableSeparator)) {
      flushParagraph();
      closeList();
      lineIndex += 2;
      const tableRows: string[][] = [];
      while (lineIndex < lines.length) {
        const row = parseTableRow(lines[lineIndex].trim());
        if (!row.length) {
          lineIndex--;
          break;
        }
        tableRows.push(row);
        lineIndex++;
      }
      html.push(renderTable(tableHeader, tableRows));
      continue;
    }

    const listItem = trimmed.match(/^([-*+]|\d+\.)\s+(.+)$/);
    if (listItem) {
      flushParagraph();
      const nextListTag = /\d+\./.test(listItem[1]) ? 'ol' : 'ul';
      if (listTag && listTag !== nextListTag) closeList();
      if (!listTag) {
        listTag = nextListTag;
        html.push(`<${listTag}>`);
      }
      html.push(`<li>${renderInline(listItem[2])}</li>`);
      continue;
    }

    closeList();
    paragraph.push(trimmed);
  }

  if (inCodeBlock) html.push(`<pre><code>${escapeHtml(codeLines.join('\n'))}</code></pre>`);
  flushParagraph();
  closeList();
  return html.join('');
}

function renderInline(text: string): string {
  return escapeHtml(text)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)]\(([^)\s]+)\)/g, (_match, label: string, href: string) => {
      const safeHref = sanitizeHref(href);
      return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    })
    .replace(/(\*\*|__)(.+?)\1/g, '<strong>$2</strong>')
    .replace(/(\*|_)([^*_]+?)\1/g, '<em>$2</em>');
}

function sanitizeHref(href: string): string {
  const decodedHref = href.replace(/&amp;/g, '&');
  return /^(https?:|mailto:|#)/i.test(decodedHref) ? escapeAttribute(decodedHref) : '#';
}

function parseTableRow(line: string): string[] {
  if (!line.includes('|')) return [];
  return line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());
}

function isTableSeparator(row: string[]): boolean {
  return row.length > 0 && row.every(cell => /^:?-{3,}:?$/.test(cell));
}

function renderTable(header: string[], rows: string[][]): string {
  const columnCount = header.length;
  const headerHtml = header.map(cell => `<th>${renderInline(cell)}</th>`).join('');
  const bodyHtml = rows.map(row => {
    const cells = Array.from({ length: columnCount }, (_value, index) => row[index] || '');
    return `<tr>${cells.map(cell => `<td>${renderInline(cell)}</td>`).join('')}</tr>`;
  }).join('');
  return `<div class="simulation-report-dialog__table-wrap"><table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/`/g, '&#96;');
}
